/**
 * API Service for ModularCompliance
 * 
 * This service provides centralized API access for all components
 * with consistent error handling and authentication management.
 */

export class ApiService {
  private static baseUrl = process.env.REACT_APP_API_URL || '/api';
  
  /**
   * Get authentication token from localStorage
   */
  private static getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  }
  
  /**
   * Set authentication headers for requests
   */
  private static getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    const token = this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Add company ID if available
    const companyId = localStorage.getItem('currentCompanyId');
    if (companyId) {
      headers['X-Company-Id'] = companyId;
    }
    
    return headers;
  }
  
  /**
   * Handle API errors consistently
   */
  private static handleError(error: any): never {
    // Log error for debugging
    console.error('API Error:', error);
    
    // Check if it's a response error with status
    if (error instanceof Response) {
      if (error.status === 401) {
        // Clear token on authentication failure
        localStorage.removeItem('auth_token');
        // Redirect to login if needed
        window.location.href = '/login';
      }
      throw new Error(`API error: ${error.status} ${error.statusText}`);
    }
    
    // Check if it's already an Error object
    if (error instanceof Error) {
      // Check for authentication error in message
      if (error.message.includes('401')) {
        localStorage.removeItem('auth_token');
        window.location.href = '/login';
      }
      throw error;
    }
    
    // Otherwise throw a generic error
    throw new Error(error?.message || 'An unexpected error occurred');
  }
  
  /**
   * Perform GET request
   */
  static async get<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
    try {
      // Add query parameters if present
      const queryParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, String(value));
          }
        });
      }
      
      const queryString = queryParams.toString();
      const url = queryString ? `${this.baseUrl}${endpoint}?${queryString}` : `${this.baseUrl}${endpoint}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
      });
      
      if (!response.ok) {
        const errorMessage = await response.text().catch(() => response.statusText);
        throw new Error(`API error: ${response.status} - ${errorMessage}`);
      }
      
      return await response.json();
    } catch (error) {
      return this.handleError(error);
    }
  }
  
  /**
   * Perform POST request
   */
  static async post<T>(endpoint: string, data: any): Promise<T> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const errorMessage = await response.text().catch(() => response.statusText);
        throw new Error(`API error: ${response.status} - ${errorMessage}`);
      }
      
      return await response.json();
    } catch (error) {
      return this.handleError(error);
    }
  }
  
  /**
   * Perform PUT request
   */
  static async put<T>(endpoint: string, data: any): Promise<T> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const errorMessage = await response.text().catch(() => response.statusText);
        throw new Error(`API error: ${response.status} - ${errorMessage}`);
      }
      
      return await response.json();
    } catch (error) {
      return this.handleError(error);
    }
  }
  
  /**
   * Perform DELETE request
   */
  static async delete<T>(endpoint: string): Promise<T> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
      });
      
      if (!response.ok) {
        const errorMessage = await response.text().catch(() => response.statusText);
        throw new Error(`API error: ${response.status} - ${errorMessage}`);
      }
      
      return await response.json();
    } catch (error) {
      return this.handleError(error);
    }
  }
  
  /**
   * Upload file
   */
  static async uploadFile<T>(endpoint: string, file: File, metadata?: Record<string, any>): Promise<T> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Add metadata if provided
      if (metadata) {
        formData.append('metadata', JSON.stringify(metadata));
      }
      
      const headers: Record<string, string> = {};
      const token = this.getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers,
        body: formData,
      });
      
      if (!response.ok) {
        const errorMessage = await response.text().catch(() => response.statusText);
        throw new Error(`API error: ${response.status} - ${errorMessage}`);
      }
      
      return await response.json();
    } catch (error) {
      return this.handleError(error);
    }
  }
}

/**
 * API endpoints for ModularCompliance
 */
export const ApiEndpoints = {
  // Authentication endpoints
  AUTH: {
    LOGIN: '/auth/login',
    PROFILE: '/auth/profile',
    REFRESH: '/auth/refresh',
  },
  
  // Dashboard endpoints
  DASHBOARD: '/dashboard',
  
  // Framework endpoints
  FRAMEWORKS: {
    LIST: '/frameworks',
    DETAIL: (id: string | number) => `/frameworks/${id}`,
    CREATE: '/frameworks',
    UPDATE: (id: string | number) => `/frameworks/${id}`,
    DELETE: (id: string | number) => `/frameworks/${id}`,
  },
  
  // Obligation endpoints
  OBLIGATIONS: {
    LIST: '/obligations',
    DETAIL: (id: string | number) => `/obligations/${id}`,
    CREATE: '/obligations',
    UPDATE: (id: string | number) => `/obligations/${id}`,
    DELETE: (id: string | number) => `/obligations/${id}`,
  },
  
  // Control endpoints
  CONTROLS: {
    LIST: '/controls',
    DETAIL: (id: string | number) => `/controls/${id}`,
    CREATE: '/controls',
    UPDATE: (id: string | number) => `/controls/${id}`,
    DELETE: (id: string | number) => `/controls/${id}`,
  },
  
  // Requirement endpoints
  REQUIREMENTS: {
    LIST: '/requirements',
    DETAIL: (id: string | number) => `/requirements/${id}`,
    CREATE: '/requirements',
    UPDATE: (id: string | number) => `/requirements/${id}`,
    DELETE: (id: string | number) => `/requirements/${id}`,
  },
  
  // Task endpoints
  TASKS: {
    LIST: '/tasks',
    DETAIL: (id: string | number) => `/tasks/${id}`,
    CREATE: '/tasks',
    UPDATE: (id: string | number) => `/tasks/${id}`,
    DELETE: (id: string | number) => `/tasks/${id}`,
  },
  
  // Document endpoints
  DOCUMENTS: {
    LIST: '/documents',
    DETAIL: (id: string | number) => `/documents/${id}`,
    UPLOAD: '/documents/upload',
    DELETE: (id: string | number) => `/documents/${id}`,
  },
};
